import { tokens } from '@fluentui/react-components'
import type { SkillCategory } from './types'

export const skillsMatrixContent = {
	title: 'Interactive Skills Matrix',
	description: 'Dynamic visualization of Microsoft 365 development expertise and skill levels',

	categories: [
		{
			name: 'SharePoint Framework (SPFx)',
			color: tokens.colorPaletteBlueBorderActive,
			skills: [
				{
					id: 'spfx-webparts',
					name: 'SPFx Web Parts',
					level: 95,
					description: 'Advanced development of modern SharePoint web parts with React.',
					sinceYear: 2019,
					// certifications: ['MS-600']
				},
				{
					id: 'spfx-extensions',
					name: 'SPFx Extensions',
					level: 90,
					description: 'Field customizers, command sets, and application customizers.',
					sinceYear: 2020,
					// certifications: ['MS-600']
				},
				{
					id: 'pnpjs',
					name: 'PnPjs Library',
					level: 88,
					description: 'Fluent JavaScript API for SharePoint REST services.',
					sinceYear: 2020
				}
			]
		},
		{
			name: 'Microsoft Teams Development',
			color: tokens.colorPalettePurpleBorderActive,
			skills: [
				{
					id: 'teams-tabs',
					name: 'Teams Tab Apps',
					level: 85,
					description: 'Custom tab applications with Microsoft Graph integration.',
					sinceYear: 2021,
					// certifications: ['MS-600']
				},
				{
					id: 'teams-bots',
					name: 'Teams Bots',
					level: 75,
					description: 'Conversational bots using Bot Framework.',
					sinceYear: 2022
				},
				{
					id: 'adaptive-cards',
					name: 'Adaptive Cards',
					level: 80,
					description: 'Interactive card-based user experiences.',
					sinceYear: 2021
				}
			]
		},
		{
			name: 'Azure Services',
			color: tokens.colorPaletteGreenBorderActive,
			skills: [
				{
					id: 'azure-functions',
					name: 'Azure Functions',
					level: 82,
					description: 'Serverless compute solutions for Microsoft 365 integration.',
					sinceYear: 2021,
					// certifications: ['AZ-204']
				},
				{
					id: 'azure-ad',
					name: 'Entra ID (Azure AD) Integration',
					level: 88,
					description: 'Authentication and authorization with Microsoft identity platform.',
					sinceYear: 2020,
					// certifications: ['MS-600', 'AZ-204']
				},
				{
					id: 'microsoft-graph',
					name: 'Microsoft Graph API',
					level: 92,
					description: 'Unified API endpoint for Microsoft 365 services',
					sinceYear: 2020,
					// certifications: ['MS-600']
				}
			]
		},
		{
			name: 'Infrastructure & DevOps',
			color: tokens.colorPaletteTealBorderActive,
			skills: [
				{
					id: 'kubernetes-aks',
					name: 'Kubernetes / Azure Kubernetes Service (AKS)',
					level: 80,
					description: 'Container orchestration on AKS — deployments, services, ingress, namespaces, RBAC.',
					sinceYear: 2024
				},
				{
					id: 'terraform',
					name: 'Terraform',
					level: 80,
					description: 'Infrastructure as Code for Azure resources — modules, state management, workspaces.',
					sinceYear: 2021
				},
				{
					id: 'rancher',
					name: 'Rancher',
					level: 77,
					description: 'Multi-cluster Kubernetes management, monitoring and access control via Rancher UI.',
					sinceYear: 2024
				},
				{
					id: 'argocd',
					name: 'Argo CD',
					level: 77,
					description: 'GitOps continuous delivery for Kubernetes — app of apps, sync policies, rollbacks.',
					sinceYear: 2024
				},
				{
					id: 'splunk',
					name: 'Splunk',
					level: 80,
					description: 'Operational data analysis and custom dashboards — log ingestion, SPL queries, alerting and real-time visualization of infrastructure and application metrics.',
					sinceYear: 2020
				},
				{
					id: 'networking',
					name: 'Infra & Networking',
					level: 85,
					description: 'Before moving to development I worked as systems & network engineer for several years, so I have solid understanding of networking concepts, protocols and troubleshooting.',
					sinceYear: 2002
				}
			]
		},
		{
			name: 'Development Technologies',
			color: tokens.colorPaletteDarkOrangeBorderActive,
			skills: [
				{
					id: 'react-typescript',
					name: 'React / qwik + TypeScript',
					level: 90,
					description: 'Modern frontend development with type safety.',
					sinceYear: 2018
				},
				{
					id: 'csharp-dotnet',
					name: 'C# / .NET Core',
					level: 85,
					description: 'Backend API development and Azure integrations. REST, SOAP, OAuth.',
					sinceYear: 2009
				},
				{
					id: 'devops',
					name: 'DevOps: CI/CD / Docker / GitHub Actions',
					level: 85,
					description: 'Containerization and continuous integration/continuous deployment.',
					sinceYear: 2019
				},
				{
					id: 'testing',
					name: 'Selenium / Playwright / Jest / Vitest',
					level: 80,
					description: 'Automated testing frameworks for web applications.',
					sinceYear: 2018
				},
				{
					id: 'powershell-pnp',
					name: 'PowerShell & PnP',
					level: 90,
					description: 'SharePoint provisioning, automation and administration scripts.',
					sinceYear: 2017
				},
				{
					id: 'source-control',
					name: 'Source Control: Git / GitHub / BitBucket',
					level: 90,
					description: 'SharePoint automation and administration scripts.',
					sinceYear: 2017
				}
			]
		},
		{
			name: 'AI & Agentic Development',
			color: tokens.colorPaletteBerryBorderActive,
			skills: [
				{
					id: 'ai-agents',
					name: 'AI Coding Agents',
					level: 85,
					description: 'Prompting, instructing and running coding agents — planning, context engineering, reviewing and steering agent output to production quality.',
					sinceYear: 2024,
					certifications: ['AI Dev Course 2024', 'AI Dev Course 2025']
				},
				{
					id: 'ai-architecture',
					name: 'LLM & Agent Architecture',
					level: 80,
					description: 'Solid understanding of LLM-based system architecture — context management, tool calling, RAG and multi-agent orchestration.',
					sinceYear: 2024
				},
				{
					id: 'ai-sdlc',
					name: 'AI in the Development Process',
					level: 85,
					description: 'Applying AI where it adds real value to software development: refactoring, design, simplification and documentation — with human review where it matters.',
					sinceYear: 2024
				}
			]
		},
		{
			name: 'Soft Skills',
			color: tokens.colorPalettePinkForeground2,
			skills: [
				{
					id: 'cooperation',
					name: 'Reliability and Result-oriented Cooperation',
					level: 95,
					description: 'Introvert by nature who knows how to communicate and cooperate effectively with different stakeholders and team members to achieve common goals.',
					sinceYear: 1999
				},
				{
					id: 'creativity',
					name: 'Creativity, Problem-solving and Continuous Learning',
					level: 95,
					description: 'I enjoy finding innovative solutions to complex problems and I am committed to continuous learning and improvement. I prefer to automate repetitive tasks whenever possible and move on to more interesting challenges.',
					sinceYear: 1999
				},
				{
					id: 'agile-scrum',
					name: 'Agile / Scrum / Kanban',
					level: 85,
					description: 'Agile methodologies for iterative development and continuous improvement.',
					sinceYear: 2014
				},
				{
					id: 'ux-ui-design',
					name: 'UX/UI Design Principles',
					level: 85,
					description: 'User-centered design principles for intuitive interfaces.',
					sinceYear: 2019
				},
				{
					id: 'documentation',
					name: 'Documentation - Confluence / Markdown / JIRA',
					level: 90,
					description: 'I actually like to document what I built because I acknowledge human memory has limits and I like to make life easier for my colleagues and my future self. No fan on info silos.',
					sinceYear: 2017
				},
				{
					id: 'users',
					name: 'User Training & Support',
					level: 90,
					description: 'I know when to speak tech and non-tech language and be patient when explaining complex concepts.',
					sinceYear: 2004
				},
				{
					id: 'project-management',
					name: 'Project Management - JIRA / Gitlab',
					level: 90,
					description: 'Project management tools for task tracking and collaboration.',
					sinceYear: 2016
				}
			]
		}
	] as SkillCategory[]
}